import { AdminHeaderActionButton, AdminPageHeader } from '@/components/patterns/admin-page'
import {
  CollectionMembershipBadge,
  CollectionOriginBadge,
  getCollectionMembershipBadgeToneClassName,
  getCollectionOriginBadgeToneClassName,
} from '@/components/collections/CollectionBadges'
import { PriceChangeAlert } from '@/components/products/price-changes/PriceChangeAlert'
import { InfoNoticeAction } from '@/components/patterns/info-notice'
import CollectionForm from '@/components/forms/CollectionForm'
import { Badge } from '@/components/ui/badge'
import { Typography } from '@/components/ui/typography'
import useIsGlobalOrg from '@/hooks/useIsGlobalOrg'
import { usePathHelper } from '@/hooks/usePathHelper'
import { adminRouteTitleHead } from '@/lib/admin-route-title'
import { Can, useAbility } from '@/lib/casl'
import { collectionQueries, useCollection } from '@/lib/services/collections/collections.query'
import { collectionSchema } from '@/lib/services/collections/collections.schema'
import { getPriceAdjustmentActionLabel } from '@/lib/services/price-adjustments/priceAdjustments.summary'
import { isSubscriptionPackageCollection } from '@/lib/subscription-package-collection'
import { createFileRoute, Link, notFound } from '@tanstack/react-router'
import { ChevronRight, LibraryBig, Printer, Tags } from 'lucide-react'

export const Route = createFileRoute('/_authenticated/$orgId/products/collections/$collectionId')({
  component: CollectionDetailPage,
  head: adminRouteTitleHead,
  params: { parse: (params) => collectionSchema.pick({ collectionId: true }).parse(params) },
  loader: async ({ context, params }) => {
    if (!context.ability.can('view', 'collections')) throw notFound()

    const collection = await context.queryClient.ensureQueryData(
      collectionQueries.show({
        organizationId: params.orgId,
        collectionId: params.collectionId,
      })
    )
    return { collection, isReadOnly: !context.ability.can('edit', 'collections') }
  },
})

function getCollectionPriceChangeBannerTitle(adjustment) {
  return adjustment?.scope_type === 'global'
    ? 'Global price change active right now'
    : 'Collection price change active right now'
}

function getCollectionPriceChangeBannerDescription(adjustment) {
  return adjustment?.scope_type === 'global'
    ? 'Products in this collection are currently using the active global price change.'
    : 'Products in this collection are currently using this collection-specific price change.'
}

function getCollectionPriceChangeBannerActionLabel(adjustment) {
  return adjustment?.scope_type === 'global'
    ? 'View global price change'
    : 'View collection price change'
}

export function CollectionDetailPage() {
  const { collectionId } = Route.useParams()
  const { data: collection } = useCollection(collectionId)
  const path = usePathHelper()
  const isGlobalOrg = useIsGlobalOrg()
  const ability = useAbility()
  const isSubscriptionCollection = isSubscriptionPackageCollection(collection)
  const activePriceAdjustment = isSubscriptionCollection
    ? null
    : collection?.active_price_adjustment
  const canEditPriceChanges =
    ability.can('edit', 'products') && !isGlobalOrg && !isSubscriptionCollection

  return (
    <div className="max-w-4xl w-full space-y-4">
      <AdminPageHeader
        icon={LibraryBig}
        title={collection?.name}
        badges={
          <>
            <Badge variant={collection?.is_published ? 'success' : 'warning'}>
              {collection?.is_published ? 'Visible' : 'Hidden'}
            </Badge>
            <CollectionOriginBadge
              collection={collection}
              className={getCollectionOriginBadgeToneClassName(collection)}
            />
            <CollectionMembershipBadge
              collection={collection}
              automaticLabel="Fills automatically"
              manualLabel="Filled by hand"
              className={getCollectionMembershipBadgeToneClassName({ collection })}
            />
          </>
        }
        actions={
          !isGlobalOrg && !isSubscriptionCollection ? (
            <>
              <Can do="list" on="products">
                <Can do="list" on="collections">
                  <AdminHeaderActionButton variant="outline" asChild>
                    <Link {...path.catalogBuilder.index().search({ collectionId }).link}>
                      <Printer />
                      Print Catalog
                    </Link>
                  </AdminHeaderActionButton>
                </Can>
              </Can>
              <Can do="edit" on="products">
                <AdminHeaderActionButton variant="outline" asChild>
                  <Link {...path.priceChanges.create().search({ collectionId }).link}>
                    <Tags />
                    New Price Change
                  </Link>
                </AdminHeaderActionButton>
              </Can>
            </>
          ) : null
        }
      />
      {activePriceAdjustment ? (
        <PriceChangeAlert
          title={getCollectionPriceChangeBannerTitle(activePriceAdjustment)}
          action={
            canEditPriceChanges ? (
              <InfoNoticeAction {...path.priceChanges.show(activePriceAdjustment.id).link}>
                {getCollectionPriceChangeBannerActionLabel(activePriceAdjustment)}
                <ChevronRight className="size-3.5" />
              </InfoNoticeAction>
            ) : null
          }
        >
          <>
            <Typography variant="label" className="block">
              {getPriceAdjustmentActionLabel(activePriceAdjustment)}.
            </Typography>
            <Typography variant="label" className="block">
              {getCollectionPriceChangeBannerDescription(activePriceAdjustment)}
            </Typography>
          </>
        </PriceChangeAlert>
      ) : null}
      <CollectionForm initialData={collection} showPageTreatment />
    </div>
  )
}
